<template>
  <transition name="fade">
    <div v-if="isOpen" class="modal-overlay" @click.self="handleClose">
      <div class="extrusion-select-orders-modal animation-open-modal">
        <!-- Top Header -->
        <div class="modal-header-custom">
          <div class="header-info d-flex align-items-center">
            <div class="resource-icon-circle mr-1">
              <feather-icon icon="LayersIcon" size="26" />
            </div>
            <div>
              <h4 class="modal-title m-0">Selecionar Ordem de Produção</h4>
              <span class="resource-subtitle">
                Recurso: <strong>{{ resourceName }}</strong> ({{ resourceCode }})
              </span>
            </div>
          </div>
          <button class="btn-close-modal" @click="handleClose" title="Fechar">
            <feather-icon icon="XIcon" size="24" />
          </button>
        </div>

        <!-- Tabs Header (Não iniciadas > Extrudando > Em corte > Finalizadas) -->
        <div class="modal-tabs-header">
          <!-- 1. Não iniciadas -->
          <button
            type="button"
            class="modal-tab-btn tab-btn-unstarted"
            :class="{ active: activeTab === 'unstarted' }"
            @click="changeTab('unstarted')"
          >
            <feather-icon icon="ClockIcon" size="18" class="mr-50" />
            <span>Não iniciadas</span>
            <span class="tab-count-badge badge-unstarted ml-50">{{ loading && rawOrders.length === 0 ? '-' : unstartedOrdersCount }}</span>
          </button>

          <!-- 2. Extrudando -->
          <button
            type="button"
            class="modal-tab-btn tab-btn-extruding"
            :class="{ active: activeTab === 'extruding' }"
            @click="changeTab('extruding')"
          >
            <feather-icon icon="PlayCircleIcon" size="18" class="mr-50" />
            <span>Extrudando</span>
            <span class="tab-count-badge badge-extruding ml-50">{{ loading && rawOrders.length === 0 ? '-' : extrudingOrdersCount }}</span>
          </button>

          <!-- 3. Em corte (com ícone de serra circular) -->
          <button
            type="button"
            class="modal-tab-btn tab-btn-cutting"
            :class="{ active: activeTab === 'cutting' }"
            @click="changeTab('cutting')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="feather mr-50 icon-saw-blade"
            >
              <path d="M20,15C20,15 18.6,16.3 21.1,17L18.3,19.8H15.5C15.5,19.8 13.6,19.7 15,22H11L9,20C9,20 7.7,18.6 7,21.1L4.2,18.3V15.5C4.2,15.5 4.3,13.6 2,15V11L4,9C4,9 5.4,7.7 2.8,7.1L5.6,4.2H8.5C8.5,4.2 10.4,4.3 9,2H13L15,4C15,4 16.3,5.4 17,2.8L19.8,5.6V8.5C19.8,8.5 19.7,10.4 22,9V13L20,15M14,12A2,2 0 0,0 12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12Z" />
            </svg>
            <span>Em corte</span>
            <span class="tab-count-badge badge-cutting ml-50">{{ loading && rawOrders.length === 0 ? '-' : cuttingOrdersCount }}</span>
          </button>

          <!-- 4. Finalizadas -->
          <button
            type="button"
            class="modal-tab-btn tab-btn-finished"
            :class="{ active: activeTab === 'finished' }"
            @click="changeTab('finished')"
          >
            <feather-icon icon="CheckCircleIcon" size="18" class="mr-50" />
            <span>Finalizadas</span>
            <span class="tab-count-badge badge-finished ml-50">{{ loading && rawOrders.length === 0 ? '-' : finishedOrdersCount }}</span>
          </button>
        </div>

        <!-- Filter & Search Bar -->
        <div class="modal-filters-bar">
          <div class="row align-items-center">
            <!-- Search Box -->
            <div class="col-12 col-md-6 mb-1 mb-md-0">
              <div class="search-input-box">
                <feather-icon icon="SearchIcon" size="18" class="search-icon" />
                <input
                  type="text"
                  v-model="searchQuery"
                  class="search-input"
                  placeholder="Buscar por número da OP, produto ou status..."
                  @input="handleSearchInput"
                  @keydown.enter="handleSearchEnter"
                />
                <button v-if="searchQuery" class="clear-search-btn" @click="clearSearch" title="Limpar busca">
                  <feather-icon icon="XCircleIcon" size="16" />
                </button>
              </div>
            </div>

            <!-- Material Dropdown Filter -->
            <div class="col-12 col-md-6 mb-1 mb-md-0">
              <div class="material-filter-wrapper w-100">
                <label for="material-select" class="filter-label mr-50 mb-0">Material:</label>
                <select
                  id="material-select"
                  v-model="selectedMaterialId"
                  class="custom-select-material"
                  @change="handleMaterialChange"
                >
                  <option value="">Todos os Materiais ({{ rawMaterials.length }})</option>
                  <option
                    v-for="mat in rawMaterials"
                    :key="mat.id"
                    :value="mat.id"
                  >
                    {{ mat.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Finished Orders Extra Filters: Situação e Ordenação -->
          <div v-if="activeTab === 'finished'" class="finished-filters-row row align-items-center mt-1 pt-1 border-top-subtle">
            <!-- Situation Filter -->
            <div class="col-12 col-md-6 mb-1 mb-md-0">
              <div class="material-filter-wrapper w-100">
                <label for="situation-select" class="filter-label mr-50 mb-0">Situação:</label>
                <select
                  id="situation-select"
                  v-model="selectedSituation"
                  class="custom-select-material"
                  @change="handleSituationChange"
                >
                  <option value="">Todas as Situações</option>
                  <option value="Aprovado">Aprovado</option>
                  <option value="Reprovado">Reprovado</option>
                  <option value="Aprovado parcial">Aprovado parcial</option>
                  <option value="Teste">Teste</option>
                </select>
              </div>
            </div>

            <!-- Sort Selector -->
            <div class="col-12 col-md-6">
              <div class="material-filter-wrapper w-100">
                <label for="sort-select" class="filter-label mr-50 mb-0">Ordenar:</label>
                <select
                  id="sort-select"
                  v-model="selectedSortBy"
                  class="custom-select-material"
                  @change="handleSortChange"
                >
                  <option value="finished_desc">Data de conclusão (Mais recentes)</option>
                  <option value="finished_asc">Data de conclusão (Mais antigas)</option>
                  <option value="op_asc">Número da OP (crescente)</option>
                  <option value="op_desc">Número da OP (decrescente)</option>
                  <option value="qty_desc">Quantidade da OP (maior primeiro)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Selection Info & Active Count Row -->
          <div class="selection-status-row d-flex justify-content-between align-items-center mt-1 pt-50">
            <div class="d-flex align-items-center">
              <span class="badge-direct-hint">
                <feather-icon icon="MousePointerIcon" size="14" class="mr-25 text-primary" />
                Clique em uma ordem para <strong>abrir diretamente</strong> o painel de apontamento
              </span>

              <span v-if="!loading && activeTab === 'extruding' && extrudingOrdersCount > 0" class="badge-active-indicator ml-1">
                <span class="pulse-dot mr-25"></span>
                <strong>{{ extrudingOrdersCount }}</strong> extrudando no recurso
              </span>
            </div>

            <div class="orders-total-count text-muted small">
              <span v-if="loading && rawOrders.length === 0">
                Carregando ordens...
              </span>
              <span v-else-if="totalCount > 0 && rawOrders.length < totalCount">
                Exibindo {{ rawOrders.length }} de {{ totalCount }} ordens {{ currentTabPluralLabel }}
              </span>
              <span v-else>
                {{ totalCount }} ordem(ns) {{ currentTabPluralLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Orders List Body with Infinite Scroll -->
        <div class="modal-body-custom" ref="modalBody" @scroll.passive="handleScroll">
          <!-- Initial Loading State -->
          <div v-if="loading && rawOrders.length === 0" class="state-container py-5 text-center">
            <b-spinner variant="warning" label="Carregando..." class="mb-1" />
            <p class="text-muted font-weight-bold">Carregando ordens de produção...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="!loading && rawOrders.length === 0" class="state-container py-5 text-center">
            <feather-icon icon="InboxIcon" size="52" class="text-muted mb-1 opacity-5" />
            <h5 class="text-dark font-weight-bold">
              {{ emptyStateTitle }}
            </h5>
            <p class="text-muted small mb-0" v-if="searchQuery || selectedMaterialId">
              Nenhuma ordem corresponde aos filtros pesquisados.
            </p>
            <p class="text-muted small mb-0" v-else>
              {{ emptyStateDescription }}
            </p>
          </div>

          <!-- Orders Grid (Direct 1-Click to Open) -->
          <div v-else class="orders-cards-grid">
            <div
              v-for="order in rawOrders"
              :key="order.uniqueKey"
              class="order-selection-card direct-click-card"
              :class="{
                'is-active-op': order.isActive
              }"
              :style="getCardBorderLeft(order)"
              @click="openOrderPanel(order)"
            >
              <!-- Card Header: OP Code + Status Badge from Backend -->
              <div class="card-header-row d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="order-code-badge">#{{ order.orderNumber || order.orderCode }}</span>
                </div>

                <!-- Status Badge (Name & Color from Backend) -->
                <span class="order-status-badge" :style="getOrderStatusStyle(order)">
                  <span
                    class="status-dot-custom"
                    :style="{ backgroundColor: order.statusColor || '#808080' }"
                  ></span>
                  {{ order.statusName || 'Não Iniciado' }}
                </span>
              </div>

              <!-- Operation / Center Pill -->
              <div class="operation-row mt-50" v-if="order.operationName">
                <span class="operation-pill">
                  <feather-icon icon="ToolIcon" size="11" class="mr-25" />
                  {{ order.operationName }}
                </span>
              </div>

              <!-- Rejection Reason Pill -->
              <div class="rejection-row mt-50" v-if="order.rejectionReason">
                <span class="rejection-pill" :title="`Motivo: ${order.rejectionReason}`">
                  <feather-icon icon="AlertTriangleIcon" size="11" class="mr-25" />
                  Motivo: {{ order.rejectionReason }}
                </span>
              </div>

              <!-- Product / Material Name -->
              <div class="order-product-name" :title="order.materialName">
                {{ order.materialName || 'Material não especificado' }}
              </div>

              <!-- Quantities Metric Row -->
              <div class="order-quantities-row">
                <div class="qty-block">
                  <span class="qty-label">Total OP</span>
                  <span class="qty-value font-weight-bold">{{ formatNumber(order.quantity) }}</span>
                </div>

                <div class="qty-block">
                  <span class="qty-label">Produzido</span>
                  <span
                    class="qty-value font-weight-bold"
                    :class="order.producedQuantity > 0 ? 'text-success' : 'text-muted'"
                  >
                    {{ formatNumber(order.producedQuantity) }}
                  </span>
                </div>

                <div class="qty-block">
                  <span class="qty-label">Saldo</span>
                  <span
                    class="qty-value font-weight-bold"
                    :class="calculateBalance(order) > 0 ? 'text-primary' : 'text-success'"
                  >
                    {{ formatNumber(calculateBalance(order)) }}
                  </span>
                </div>
              </div>

              <!-- Mini Progress Bar -->
              <div v-if="order.quantity > 0" class="progress-bar-container mt-50">
                <div
                  class="progress-bar-fill"
                  :style="{
                    width: `${Math.min(100, ((order.producedQuantity / order.quantity) * 100))}%`,
                    backgroundColor: order.statusColor || '#28C76F'
                  }"
                ></div>
              </div>

              <!-- Finished At / Completion Date-Time -->
              <div v-if="order.finishedAt" class="card-finished-at-row mt-50 d-flex align-items-center justify-content-between">
                <span class="finished-at-label text-muted small">
                  <feather-icon icon="CalendarIcon" size="12" class="mr-25 text-muted" />
                  Concluído em:
                </span>
                <span class="finished-at-value small font-weight-bold">
                  {{ formatDateTime(order.finishedAt) }}
                </span>
              </div>

              <!-- Hover Enter Button Prompt -->
              <div class="card-enter-prompt mt-1">
                <span class="enter-text">
                  Abrir Painel
                  <feather-icon icon="ArrowRightIcon" size="14" class="ml-25" />
                </span>
              </div>
            </div>
          </div>

          <!-- Infinite Scroll Loading More Spinner -->
          <div v-if="loadingMore" class="loading-more-container py-2 text-center mt-1">
            <b-spinner small variant="warning" label="Carregando..." class="mr-50" />
            <span class="text-muted font-weight-bold small">Carregando mais ordens...</span>
          </div>

          <!-- End of List Indicator -->
          <div v-else-if="!hasMore && rawOrders.length > 0 && totalCount > pageSize" class="end-of-list-container py-2 text-center text-muted small mt-1">
            <feather-icon icon="CheckIcon" size="14" class="mr-25 text-success" />
            Todas as {{ totalCount }} ordens foram carregadas.
          </div>
        </div>

        <!-- Bottom Footer Actions -->
        <div class="modal-footer-custom d-flex justify-content-between align-items-center">
          <button class="btn-footer-cancel" @click="handleClose">
            Fechar
          </button>

          <div class="footer-hint-text text-muted small">
            <feather-icon icon="InfoIcon" size="14" class="mr-25 text-primary" />
            Selecione a ordem desejada para entrar no painel de apontamento
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { BSpinner } from 'bootstrap-vue';
import { getExtrusionOrders, getSelectableMaterialsForResource } from '../extrusionApi';

export default {
  name: 'SelectExtrusionOrderModal',
  components: {
    BSpinner
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    resource: {
      type: Object,
      default: () => ({})
    },
    siteId: {
      type: [Number, String],
      default: 1
    },
    beforeSelect: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      activeTab: 'unstarted', // 'unstarted' | 'extruding' | 'cutting' | 'finished'
      loading: false,
      loadingMore: false,
      rawOrders: [],
      rawMaterials: [],
      selectedMaterialId: '',
      searchQuery: '',
      searchDebounceTimeout: null,
      page: 1,
      pageSize: 24,
      totalCount: 0,
      totalPages: 1,
      unstartedOrdersCount: 0,
      extrudingOrdersCount: 0,
      cuttingOrdersCount: 0,
      finishedOrdersCount: 0,
      inProgressOrdersCount: 0,
      activeOrdersCount: 0,
      selectedSituation: '',
      selectedSortBy: 'finished_desc'
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    resourceId() {
      return this.resource?.resourceId || this.resource?.id;
    },
    resourceName() {
      return this.resource?.resourceName || this.resource?.name || 'Prensa de Extrusão';
    },
    resourceCode() {
      return this.resource?.resourceCode || this.resource?.code || '—';
    },
    hasMore() {
      return this.page < this.totalPages;
    },
    emptyStateTitle() {
      switch (this.activeTab) {
        case 'unstarted':
          return 'Nenhuma Ordem Não Iniciada';
        case 'extruding':
          return 'Nenhuma Ordem Extrudando';
        case 'cutting':
          return 'Nenhuma Ordem em Corte';
        case 'finished':
          return 'Nenhuma Ordem Finalizada';
        default:
          return 'Nenhuma Ordem Encontrada';
      }
    },
    emptyStateDescription() {
      switch (this.activeTab) {
        case 'unstarted':
          return 'Não existem ordens aguardando início para esta prensa no momento.';
        case 'extruding':
          return 'Não existem ordens em processo de extrusão nesta prensa no momento.';
        case 'cutting':
          return 'Não existem ordens na etapa de corte no momento.';
        case 'finished':
          return 'Não existem ordens finalizadas nesta prensa no momento.';
        default:
          return 'Não existem ordens de produção disponíveis no momento.';
      }
    },
    currentTabPluralLabel() {
      switch (this.activeTab) {
        case 'unstarted':
          return 'não iniciada(s)';
        case 'extruding':
          return 'extrudando';
        case 'cutting':
          return 'em corte';
        case 'finished':
          return 'finalizada(s)';
        default:
          return 'disponível(is)';
      }
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.resetState();
        if (this.resourceId) {
          this.loadData();
        }
      } else {
        this.resetState();
        this.loading = false;
      }
    }
  },
  beforeDestroy() {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  },
  methods: {
    resetState() {
      this.activeTab = 'unstarted';
      this.loadingMore = false;
      this.searchQuery = '';
      this.selectedMaterialId = '';
      this.page = 1;
      this.totalCount = 0;
      this.totalPages = 1;
      this.unstartedOrdersCount = 0;
      this.extrudingOrdersCount = 0;
      this.cuttingOrdersCount = 0;
      this.finishedOrdersCount = 0;
      this.inProgressOrdersCount = 0;
      this.activeOrdersCount = 0;
      this.selectedSituation = '';
      this.selectedSortBy = 'finished_desc';
      this.rawOrders = [];
      this.rawMaterials = [];
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
        this.searchDebounceTimeout = null;
      }
    },
    async loadData() {
      if (this.loading) return;
      this.loading = true;
      try {
        await Promise.all([
          this.fetchMaterials(),
          this.fetchOrders(false)
        ]);
      } catch (err) {
        console.error('Erro ao carregar dados do modal de ordens:', err);
      } finally {
        this.loading = false;
      }
    },
    async fetchMaterials() {
      if (!this.resourceId) return;
      try {
        const res = await getSelectableMaterialsForResource(this.resourceId, this.siteId);
        const list = res.data?.data || res.data || [];
        if (Array.isArray(list)) {
          this.rawMaterials = list.map(m => ({
            id: m.materialId || m.id,
            name: m.materialName || m.name || m.text
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar materiais:', err);
        this.rawMaterials = [];
      }
    },
    async fetchOrders(isAppend = false) {
      if (!this.resourceId) return;

      if (isAppend) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }

      try {
        const res = await getExtrusionOrders(this.resourceId, this.siteId, {
          materialId: this.selectedMaterialId || null,
          search: this.searchQuery?.trim() || null,
          tab: this.activeTab,
          situation: this.activeTab === 'finished' ? (this.selectedSituation || null) : null,
          sortBy: this.activeTab === 'finished' ? (this.selectedSortBy || null) : null,
          page: this.page,
          pageSize: this.pageSize
        });

        const responseData = res.data || {};
        const list = Array.isArray(responseData.data)
          ? responseData.data
          : (Array.isArray(responseData) ? responseData : []);

        this.totalCount = responseData.totalCount !== undefined ? responseData.totalCount : list.length;
        this.totalPages = responseData.totalPages !== undefined ? responseData.totalPages : 1;

        if (responseData.unstartedCount !== undefined) {
          this.unstartedOrdersCount = responseData.unstartedCount;
        } else if (responseData.inProgressCount !== undefined && responseData.activeCount !== undefined) {
          this.unstartedOrdersCount = Math.max(0, responseData.inProgressCount - responseData.activeCount);
        }

        if (responseData.extrudingCount !== undefined) {
          this.extrudingOrdersCount = responseData.extrudingCount;
        } else if (responseData.activeCount !== undefined) {
          this.extrudingOrdersCount = responseData.activeCount;
        }

        if (responseData.cuttingCount !== undefined) {
          this.cuttingOrdersCount = responseData.cuttingCount;
        }
        if (responseData.finishedCount !== undefined) {
          this.finishedOrdersCount = responseData.finishedCount;
        }
        if (responseData.inProgressCount !== undefined) {
          this.inProgressOrdersCount = responseData.inProgressCount;
        }
        if (responseData.activeCount !== undefined) {
          this.activeOrdersCount = responseData.activeCount;
        }

        const mapped = list.map(o => {
          let statusName = o.statusName || 'Não Iniciado';
          let statusColor = o.statusColor || '#808080';
          const situation = o.situation || '';
          if (situation.trim().toLowerCase() === 'teste' || statusName.trim().toLowerCase() === 'teste') {
            statusColor = '#82868B';
          }
          return {
            id: o.orderId || o.id,
            orderId: o.orderId || o.id,
            orderNumber: o.orderNumber || o.orderCode || String(o.orderId || o.id),
            orderCode: o.orderNumber || o.orderCode || String(o.orderId || o.id),
            operationId: o.operationId,
            operationName: o.operationName || o.operationNumber || '',
            materialId: o.materialId,
            materialName: o.materialName || 'Material não especificado',
            quantity: Number(o.quantity) || 0,
            producedQuantity: Number(o.producedQuantity) || 0,
            statusId: o.statusId || 1,
            statusName,
            statusColor,
            isActive: Boolean(o.isActive),
            situation,
            rejectionReason: o.rejectionReason || '',
            finishedAt: o.finishedAt || null,
            uniqueKey: `${o.orderId || o.id}-${o.operationId || 0}`
          };
        });

        if (isAppend) {
          const existingKeys = new Set(this.rawOrders.map(x => x.uniqueKey));
          const newItems = mapped.filter(x => !existingKeys.has(x.uniqueKey));
          this.rawOrders = [...this.rawOrders, ...newItems];
        } else {
          this.rawOrders = mapped;
          this.$nextTick(() => {
            if (this.$refs.modalBody) {
              this.$refs.modalBody.scrollTop = 0;
            }
          });
        }
      } catch (err) {
        console.error('Erro ao buscar ordens de extrusão:', err);
        if (!isAppend) {
          this.rawOrders = [];
        }
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    changeTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.page = 1;
      this.rawOrders = [];
      if (tab === 'finished') {
        this.selectedSortBy = 'finished_desc';
        this.selectedSituation = '';
      }
      this.fetchOrders(false);
    },
    handleSituationChange() {
      this.page = 1;
      this.rawOrders = [];
      this.fetchOrders(false);
    },
    handleSortChange() {
      this.page = 1;
      this.rawOrders = [];
      this.fetchOrders(false);
    },
    handleMaterialChange() {
      this.page = 1;
      this.rawOrders = [];
      this.fetchOrders(false);
    },
    handleSearchInput() {
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
      }
      this.searchDebounceTimeout = setTimeout(() => {
        this.page = 1;
        this.fetchOrders(false);
      }, 350);
    },
    handleSearchEnter() {
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
      }
      this.page = 1;
      this.fetchOrders(false);
    },
    clearSearch() {
      this.searchQuery = '';
      this.page = 1;
      this.fetchOrders(false);
    },
    handleScroll(e) {
      const el = e.target;
      if (!el || this.loading || this.loadingMore || !this.hasMore) return;
      const tolerance = 200;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - tolerance) {
        this.page += 1;
        this.fetchOrders(true);
      }
    },
    calculateBalance(order) {
      const total = Number(order.quantity) || 0;
      const produced = Number(order.producedQuantity) || 0;
      return Math.max(0, total - produced);
    },
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return '0';
      const num = Number(val);
      return Number.isInteger(num)
        ? num.toLocaleString('pt-BR')
        : num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatDateTime(val) {
      if (!val) return '';
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return String(val);
      }
    },
    getOrderStatusStyle(order) {
      const rawColor = (order.statusColor || '#808080').trim();
      let bg = `${rawColor}1A`;
      if (rawColor.startsWith('#')) {
        let hex = rawColor.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        if (hex.length >= 6) {
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            bg = `rgba(${r}, ${g}, ${b}, 0.12)`;
          }
        }
      }
      return {
        backgroundColor: bg,
        color: rawColor,
        border: `1px solid ${rawColor}`
      };
    },
    getCardBorderLeft(order) {
      if (order.isActive) {
        return {
          borderLeft: `5px solid ${order.statusColor || '#28C76F'}`
        };
      }
      return {};
    },
    handleClose() {
      this.isOpen = false;
      this.resetState();
      this.$emit('close');
    },
    async openOrderPanel(order) {
      if (!order) return;

      if (typeof this.beforeSelect === 'function') {
        const canProceed = await this.beforeSelect(order);
        if (!canProceed) return;
      }

      const prefix = this.$route.params.prefix || 'sfc';

      this.$emit('confirmed', {
        resource: this.resource,
        selectedOrder: order,
        orderId: order.orderId,
        operationId: order.operationId
      });

      this.isOpen = false;
      this.resetState();

      const targetRoute = {
        name: 'operator-extrusion-panel',
        params: {
          prefix,
          resourceId: this.resourceId
        },
        query: {
          orderId: order.orderId,
          operationId: order.operationId
        }
      };

      if (this.$route.name === 'operator-extrusion-panel' &&
          String(this.$route.query.orderId) === String(order.orderId) &&
          String(this.$route.query.operationId) === String(order.operationId)) {
        return;
      }

      this.$router.push(targetRoute).catch(err => {
        if (err && err.name !== 'NavigationDuplicated') {
          console.error(err);
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.68);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.extrusion-select-orders-modal {
  background: #ffffff;
  border-radius: 14px;
  width: 95vw;
  max-width: 1160px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.28);
  overflow: hidden;
  animation: modalScale 0.22s ease-out;
}

@keyframes modalScale {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Modal Header */
.modal-header-custom {
  background: linear-gradient(135deg, #974900 0%, #683200 100%);
  color: #ffffff;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  .resource-icon-circle {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.16);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
  }

  .modal-title {
    font-family: "Raleway", sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
  }

  .resource-subtitle {
    font-size: 14px;
    color: #ffd8b8;
  }

  .btn-close-modal {
    background: transparent;
    border: none;
    color: #ffffff;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
    }
  }
}

/* Modal Tabs Header */
.modal-tabs-header {
  display: flex;
  background: #f7f2ed;
  border-bottom: 1px solid #ebdcd0;
  padding: 8px 24px 0 24px;
  gap: 10px;
  flex-shrink: 0;

  .modal-tab-btn {
    display: flex;
    align-items: center;
    padding: 10px 18px;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    font-family: "Raleway", sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #7e7570;
    cursor: pointer;
    transition: all 0.2s ease;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;

    .icon-saw-blade {
      display: inline-block;
      vertical-align: middle;
      flex-shrink: 0;
    }

    &.tab-btn-unstarted {
      &:hover {
        color: #5e5873;
        background: rgba(94, 88, 115, 0.06);
      }

      &.active {
        color: #5e5873;
        background: #fdfbf9;
        border-bottom: 3px solid #5e5873;
        font-weight: 700;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.03);

        .tab-count-badge.badge-unstarted {
          background: #5e5873;
          color: #ffffff;
        }
      }
    }

    &.tab-btn-extruding {
      &:hover {
        color: #974900;
        background: rgba(151, 73, 0, 0.06);
      }

      &.active {
        color: #974900;
        background: #fdfbf9;
        border-bottom: 3px solid #974900;
        font-weight: 700;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.03);

        .tab-count-badge.badge-extruding {
          background: #974900;
          color: #ffffff;
        }
      }
    }

    &.tab-btn-cutting {
      &:hover {
        color: #7367f0;
        background: rgba(115, 103, 240, 0.06);
      }

      &.active {
        color: #7367f0;
        background: #fdfbf9;
        border-bottom: 3px solid #7367f0;
        font-weight: 700;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.03);

        .tab-count-badge.badge-cutting {
          background: #7367f0;
          color: #ffffff;
        }
      }
    }

    &.tab-btn-finished {
      &:hover {
        color: #28c76f;
        background: rgba(40, 199, 111, 0.06);
      }

      &.active {
        color: #28c76f;
        background: #fdfbf9;
        border-bottom: 3px solid #28c76f;
        font-weight: 700;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.03);

        .tab-count-badge.badge-finished {
          background: #28c76f;
          color: #ffffff;
        }
      }
    }

    .tab-count-badge {
      font-size: 12px;
      font-weight: 700;
      padding: 2px 8px;
      margin-left: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: #e2d6cc;
      color: #683200;
      transition: all 0.2s ease;

      &.badge-unstarted {
        background: #ebe9f1;
        color: #5e5873;
      }

      &.badge-extruding {
        background: #fae8d8;
        color: #974900;
      }

      &.badge-cutting {
        background: #ebe9fe;
        color: #7367f0;
      }

      &.badge-finished {
        background: #d4f4e2;
        color: #198754;
      }
    }
  }
}

/* Filters Bar */
.modal-filters-bar {
  background: #fdfbf9;
  border-bottom: 1px solid #ebdcd0;
  padding: 16px 24px;
  flex-shrink: 0;

  .search-input-box {
    position: relative;
    display: flex;
    align-items: center;

    .search-icon {
      position: absolute;
      left: 14px;
      color: #974900;
      pointer-events: none;
    }

    .search-input {
      width: 100%;
      height: 42px;
      padding: 0 34px 0 42px;
      border: 1px solid #cfc4be;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
      transition: all 0.2s;

      &:focus {
        border-color: #974900;
        box-shadow: 0 0 0 3px rgba(151, 73, 0, 0.12);
      }
    }

    .clear-search-btn {
      position: absolute;
      right: 10px;
      background: none;
      border: none;
      color: #7e7570;
      cursor: pointer;
      padding: 2px;
    }
  }

  .material-filter-wrapper {
    display: flex;
    align-items: center;

    .filter-label {
      font-size: 13.5px;
      font-weight: 700;
      color: #4c4541;
      white-space: nowrap;
    }

    .custom-select-material {
      flex: 1;
      height: 42px;
      border: 1px solid #cfc4be;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 13.5px;
      color: #4c4541;
      background-color: #fff;
      outline: none;

      &:focus {
        border-color: #974900;
      }
    }
  }

  .badge-direct-hint {
    background: #ffede2;
    color: #974900;
    border: 1px solid #ffd2b3;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 13px;
  }

  .badge-active-indicator {
    background: #e8fadf;
    color: #28c76f;
    border: 1px solid #28c76f;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;

    .pulse-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #28c76f;
      display: inline-block;
    }
  }

  .border-top-subtle {
    border-top: 1px dashed #e2d9d2;
  }
}

/* Modal Body */
.modal-body-custom {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  min-height: 320px;

  .orders-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
    gap: 16px;
  }

  .order-selection-card {
    background: #ffffff;
    border: 1.5px solid #e2d9d2;
    border-radius: 10px;
    padding: 16px;
    cursor: pointer;
    user-select: none;
    transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;

    &:hover {
      border-color: #974900;
      background: #fffdfb;
      box-shadow: 0 8px 24px rgba(151, 73, 0, 0.16);
      transform: translateY(-3px);

      .card-enter-prompt {
        opacity: 1;
        transform: translateY(0);
      }

      .order-code-badge {
        color: #7a3b00;
      }
    }

    &.is-active-op {
      background: #fcfdfc;
      box-shadow: 0 4px 14px rgba(40, 199, 111, 0.08);

      &:hover {
        background: #f5fcf7;
        box-shadow: 0 8px 24px rgba(40, 199, 111, 0.2);
      }
    }

    .order-code-badge {
      font-family: "Raleway", sans-serif;
      font-size: 16.5px;
      font-weight: 800;
      color: #974900;
      transition: color 0.2s;
    }

    .order-status-badge {
      display: inline-flex;
      align-items: center;
      padding: 3px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 700;

      .status-dot-custom {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        margin-right: 5px;
        display: inline-block;
      }
    }

    .operation-row {
      .operation-pill {
        font-size: 11.5px;
        font-weight: 600;
        background: #f4f0ed;
        color: #5c5550;
        padding: 2px 8px;
        border-radius: 6px;
        display: inline-flex;
        align-items: center;
      }
    }

    .rejection-row {
      .rejection-pill {
        display: inline-flex;
        align-items: center;
        background: #ffebe8;
        color: #ea5455;
        border: 1px solid #f8c1bc;
        border-radius: 6px;
        font-size: 11.5px;
        font-weight: 600;
        padding: 2px 8px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .order-product-name {
      font-size: 14.5px;
      font-weight: 700;
      color: #333333;
      margin: 12px 0 10px 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .order-quantities-row {
      display: flex;
      justify-content: space-between;
      border-top: 1px solid #f2ece8;
      padding-top: 10px;
      margin-top: auto;

      .qty-block {
        display: flex;
        flex-direction: column;

        .qty-label {
          font-size: 10px;
          color: #7e7570;
          text-transform: uppercase;
          font-weight: 600;
        }

        .qty-value {
          font-size: 14px;
          color: #333;
          margin-top: 2px;
        }
      }
    }

    .progress-bar-container {
      width: 100%;
      height: 4px;
      background: #eee;
      border-radius: 2px;
      overflow: hidden;

      .progress-bar-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.3s ease;
      }
    }

    .card-finished-at-row {
      padding-top: 8px;
      margin-top: 8px;
      border-top: 1px dashed #ebdcd0;

      .finished-at-label {
        color: #7e7570;
        font-size: 11.5px;
        display: inline-flex;
        align-items: center;
      }

      .finished-at-value {
        color: #28c76f;
        font-size: 12px;
        font-weight: 700;
      }
    }

    .card-enter-prompt {
      opacity: 0;
      transform: translateY(4px);
      transition: all 0.2s ease;
      text-align: right;

      .enter-text {
        font-size: 12px;
        font-weight: 700;
        color: #974900;
        display: inline-flex;
        align-items: center;
      }
    }
  }
}

/* Modal Footer */
.modal-footer-custom {
  background: #fdfbf9;
  border-top: 1px solid #ebdcd0;
  padding: 16px 24px;
  flex-shrink: 0;

  .btn-footer-cancel {
    background: #fff;
    border: 1px solid #cfc4be;
    color: #4c4541;
    font-weight: 600;
    font-size: 14.5px;
    padding: 10px 24px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f5f5f5;
    }
  }

  .footer-hint-text {
    font-size: 13px;
    color: #7e7570;
  }
}
</style>
